import type { BreadcrumbList, OnlineStore, WithContext, Product } from 'schema-dts';
import type { Variant } from '../components/Product/types/product';

export const useSeo = () => {
  const { $domain, $i18n, runWithContext } = useNuxtApp();
  const route = useRoute();

  const baseUrl = computed(() => `https://${$domain.host}`);

  const setOverallInfo = () => runWithContext(() => {
    useJsonld((): WithContext<OnlineStore> => ({
      '@context': 'https://schema.org',
      '@type': 'OnlineStore',
      'name': 'KiK',
      'url': baseUrl.value,
      'logo': `${baseUrl.value}/img/KiK_Logo.svg`,
      'image': `${baseUrl.value}/img/KiK_Logo.svg`,
      'description': $i18n.t('© KiK Textiles and Non-Food GmbH {year}', { year: new Date().getFullYear() }),
      'hasMerchantReturnPolicy': {
        '@type': 'MerchantReturnPolicy',
        'name': `KiK ${$i18n.t('Return')}`,
        'merchantReturnDays': $domain.maxAllowedDaysToReturn,
        'url': `${baseUrl.value}/${$i18n.t('footerReturnOrder')}`,
        'returnPolicyCategory': 'MerchantReturnFiniteReturnWindow',
        'returnPolicyCountry': $domain.countryCode,
        'applicableCountry': $domain.countryCode,
        'refundType': 'FullRefund',
        'returnFees': 'FreeReturn',
        'returnMethod': ['ReturnByMail', 'ReturnInStore'],
      },
    }));

    useHead({
      htmlAttrs: {
        lang: $domain.locales?.[0].split('-')[0],
      },
    });

    const seoTitle = $i18n.t('KiK Onlineshop | Cheap fashion, decoration & more! Cheap fashion, home, garden and technology trends on {host}', { host: $domain.host });

    useSeoMeta({
      title: seoTitle,
      description: seoTitle,
      ogTitle: seoTitle,
      ogDescription: seoTitle,
      ogUrl: () => `${baseUrl.value}${route.path}`,
      ogSiteName: $i18n.t('Shop online at KiK | More than you think'),
      ogImage: `${baseUrl.value}/img/KiK_Logo.svg`,
    });
  });

  const setProductInfo = ({ product, selectedVariant, productGallery }: { product: Ref<any>; selectedVariant: Ref<Variant>; productGallery: Ref<any[]> }) => runWithContext(() => {
    useSeoMeta({
      title() {
        const textBits = [product.value?.name, product.value?.brandName, selectedVariant.value?.attributes?.detailname].filter(Boolean);

        const seoText = `${textBits.join(', ')} (Art. ${product.value?.key ?? ''})`;
        return $i18n.t('{text} | KiK Onlineshop', { text: seoText });
      },
      description() {
        const textBits = [product.value?.name, product.value?.brandName, selectedVariant.value?.attributes?.detailname].filter(Boolean);

        const seoText = `${textBits.join(', ')} (Art. ${product.value?.key ?? ''})`;
        return `${seoText} ✓ ${$i18n.t('Always shop cheaply in the online shop')} | ${$i18n.t('Great offers now on KiK')}`;
      },
      appleMobileWebAppTitle() {
        const textBits = [product.value?.name, product.value?.brandName, selectedVariant.value?.attributes?.detailname].filter(Boolean);

        const seoText = `${textBits.join(', ')} (Art. ${product.value?.key ?? ''})`;
        return $i18n.t('{text} | KiK Onlineshop', { text: seoText });
      },

      ogTitle: () => product.value?.name,
      ogDescription: () => [product.value?.brandName, selectedVariant.value?.attributes?.detailname].filter(Boolean).join(', '),
      ogUrl: () => `${baseUrl.value}${route.path}`,
      ogImage: () => productGallery.value?.[0]?.url,
      ogLocale: $domain.locales[0].replace('-', '_'),
    });

    useJsonld((): WithContext<Product> | null => {
      if (!product || !selectedVariant) {
        return null;
      }

      return {
        '@context': 'https://schema.org',
        '@type': 'Product',
        'sku': product.value?.sku,
        'name': product.value?.name,
        'brand': product.value?.brandName,
        'description': product.value?.description,
        'material': selectedVariant.value?.attributes?.material?.join(', '),
        'color': selectedVariant.value?.attributes?.color,
        'size': selectedVariant.value?.attributes?.size,
        'hasMerchantReturnPolicy': {
          '@type': 'MerchantReturnPolicy',
          'name': `KiK ${$i18n.t('Return')}`,
          'merchantReturnDays': $domain.maxAllowedDaysToReturn,
          'url': `${baseUrl.value}/${$i18n.t('footerReturnOrder')}`,
          'returnPolicyCategory': 'MerchantReturnFiniteReturnWindow',
          'returnPolicyCountry': $domain.countryCode,
          'applicableCountry': $domain.countryCode,
          'refundType': 'FullRefund',
          'returnFees': 'FreeReturn',
          'returnMethod': ['ReturnByMail', 'ReturnInStore'],
        },
        'additionalProperty': selectedVariant.value?.attributes?.bulletpoints?.map((bulletPoint: string, i: number) => ({
          '@type': 'PropertyValue',
          'name': `Product Attribute ${i + 1}`,
          'value': bulletPoint,
        })) ?? undefined,
        'category': product.value?.categories?.map((category: NavbarCategory) => category.name).join(' / '),
        'image': productGallery.value?.map(({ url }) => url),
        'offers': {
          '@type': 'Offer',
          'availability': (selectedVariant.value?.availability?.availableQuantity > 0 && selectedVariant.value?.availability.isOnStock) ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          'price': selectedVariant.value?.price?.final,
          'priceCurrency': $domain.currency,
        },
      };
    });

    useHead({
      link: [{
        rel: 'canonical',
        href: `${baseUrl.value}${route.path}`,
      }],
    });
  });

  const setBreadcrumbsInfo = (breadcrumbs: any[]) => runWithContext(() => {
    useJsonld((): WithContext<BreadcrumbList> => ({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': breadcrumbs?.map(({ text, link }, i) => ({
        '@type': 'ListItem',
        'position': i + 1,
        'name': text,
        ...(link ? { item: `${baseUrl.value}${link} }` } : {}),
      })),
    }));
  });

  const setContentPageInfo = (content: ComputedRef<any | undefined>) => runWithContext(() => {
    const contentMeta = content.value?.meta?.['@nodes']?.reduce((acc: Record<string, string>, node: string) => {
      if (content.value.meta[node]?.name && content.value.meta[node]?.content) {
        acc[content.value.meta[node].name] = content.value.meta[node].content;
      }
      return acc;
    }, {}) ?? {};

    const searchSeo = route.path === '/search' ? { robots: 'noindex, follow' } : {};
    useSeoMeta({
      title: `${content.value?.title ?? content.value?.['@name']} | KiK Onlineshop`,
      description: `${content.value?.title ?? content.value?.['@name']} ✓ KiK Onlineshop`,
      ogTitle: `${content.value?.title ?? content.value?.['@name']} | KiK Onlineshop`,
      ogDescription: `${content.value?.title ?? content.value?.['@name']} ✓ ${$i18n.t('Great offers now on KiK')}`,
      appleMobileWebAppTitle: `${content.value?.title ?? content.value?.['@name']} | KiK Onlineshop`,
      ...(content.value?.seoExcluded ? { robots: 'noindex' } : searchSeo),
      ...($domain
        ? {
            ogUrl: () => `${baseUrl.value}${route.fullPath}`,
            ogImage: `${baseUrl.value}/img/KiK_Logo.svg`,
            ogLocale: $domain.locales?.[0]?.replace('-', '_'),
          }
        : {}),

      ...contentMeta,
    });

    useHead({
      link: [{
        rel: 'canonical',
        href: `${baseUrl.value}${route.path}`,
      }],
    });
  });

  const setCategoryInfo = (category: ComputedRef<any>) => runWithContext(() => {
    const searchSeo = route.path === '/search' ? { robots: 'noindex, follow' } : {};
    useHead({
      link: [
        {
          rel: 'canonical',
          href: category.value?.canonicalUrl ?? `https://${$domain.host}${route.path}`,

        },

      ],
    });

    useSeoMeta({
      ...searchSeo,
    });
  });

  const setSeoTitle = (title: string) => runWithContext(() => {
    useSeoMeta({
      title: $i18n.t(title),
      description: $i18n.t(title),
      ogTitle: $i18n.t(title),
      ogDescription: $i18n.t(title),
    });
  });

  return {
    setOverallInfo,
    setProductInfo,
    setBreadcrumbsInfo,
    setContentPageInfo,
    setCategoryInfo,
    setSeoTitle,
  };
};
